import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PendingSaleModal from './PendingSaleModal';
import { useCustomer } from '../../../hooks/useCustomer';
import { useSale } from '../../../hooks/sale/useSale';
import { useDocumentType } from '../../../hooks/useDocumentType';
import AdvancedRecordTable from '../../../components/Tables/advancedRecord/AdvancedRecordTable';
import { useBranch } from '../../../hooks/branches/useBranch';
import SaleViewModal from './SaleViewModal';
import PdfModal from '../../../components/Modals/pdf/PdfModal';
import { useSalesButtonActions } from '../../../hooks/sale/useSalesButtonConfig';
import { usePdfType } from '../../../hooks/pdf/usePdfType';
import { useSalePdfModalActions } from '../../../features/sale/history/salePdfModalActions';
import { getSaleTableActions } from '../../../features/sale/history/saleHistoryTableActions';
import { getSaleHistoryFilters } from '../../../features/sale/history/saleHistoryFilters';
import { saleHistoryColumns } from '../../../features/sale/history/saleHistoryColumns';
import { buildSaleFilterPayload } from '../../../types/sale/filters/saleFilterUtils';
import { useSalePreviewHandler } from '../../../hooks/pdf/useSalePreviewHandler';
import { useSaleInvalidation } from '../../../hooks/sale/useSaleInvalidation';
import { useAllowedOptions } from '../../../hooks/settings/useAllowedOptions';
import DtePdfModal from '../../../components/Modals/DtePdfModal/DtePdfModal';
import { useSaleDtePreview } from '../../../hooks/sale/useSaleDtePreview';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import { usePrintService } from '../../../hooks/printing/usePrintService';
import { useSalePermissions } from '../../../hooks/sale/useSalePermissions';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import { useSaleEmail } from '../../../hooks/email/useSaleEmail';
import DteErrorModal from '../../../components/Modals/DteErrorModal';
import SaleSupportModal, {
  SaleSupportPatch,
} from '../../../components/Modals/SaleSupportModal';
import SaleService from '../../../services/sale/saleService';
import { DteErrorDetail } from '../../../types/dte/DteErrorDetail';
import useAlerts from '../../../hooks/useAlerts';
import ReturnEventService from '../../../services/fiscal/ReturnEventService';
import SpecialOperationsService from '../../../services/specialOperations/SpecialOperationsService';
import SpecialOperationFromSaleModal from '../../../components/FiscalEvents/SpecialOperationFromSaleModal';

const SaleHistory: React.FC = () => {
  const navigate = useNavigate();
  const {
    handlerGetFilteredSales,
    filteredSaleList,
    paginator,
    pageSize,
    updatePageSize,
    handlerViewSaleById,
    saleToViewLoaded,
    handleCloseViewModal,
    pendingSaleList,
    pendingSaleCount,
    handlerGetPendingCount,
    handlerGetAllPendingSummary,
    clearFilteredSales,
    showSalePdfModal,
    openSalePdfModal,
    closeSalePdfModal,
    handlerGetSaleToContinue,
    showPendingSaleModal,
    openPendingSaleModal,
    closePendingSaleModal,
    setSaleMetaData,
    clearSaleMetaData,
    closeDtePdfModal,
    showDtePdfModal,
    handlerGetSaleForEdit,
  } = useSale();

  const { requestLegacyPrint } = usePrintService();

  const { loadPrefillFromSale } = useSaleInvalidation();
  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { handlerGetCustomersForDropdown, customersDropdown } = useCustomer();
  const { fetchPdfTypeDropdown, pdfTypeDropdown } = usePdfType();
  const {
    handlerConsultDTE,
    handlerDownloadJson,
    handlerForceAnnulRejectedSale,
    handlerDeleteRejectedSale,
    handlerResendDTE,
  } = useSalesButtonActions();
  const pdfModalActions = useSalePdfModalActions();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();
  const permissions = useSalePermissions();
  const { sendSaleEmail } = useSaleEmail();
  const { show: showAlert } = useAlerts();
  const [dteErrorOpen, setDteErrorOpen] = useState(false);
  const [dteErrorLoading, setDteErrorLoading] = useState(false);
  const [dteErrorUnavailable, setDteErrorUnavailable] = useState(false);
  const [dteError, setDteError] = useState<DteErrorDetail | null>(null);
  const [supportSaleId, setSupportSaleId] = useState<number | null>(null);
  const [specialOperationSaleId, setSpecialOperationSaleId] = useState<
    number | null
  >(null);
  const [returnEligibility, setReturnEligibility] = useState<
    Record<number, boolean>
  >({});
  const [specialOperationEligibility, setSpecialOperationEligibility] =
    useState<Record<number, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    const candidates = filteredSaleList.filter(
      (sale) => sale.status === 'FINALIZED' || sale.status === 'DTE_PROCESSED'
    );
    void Promise.all(
      candidates.map(async (sale) => {
        const [returnResult, simplified, internal] = await Promise.allSettled([
          ReturnEventService.eligibility(sale.id),
          SpecialOperationsService.prefillFromSale(
            sale.id,
            'SIMPLIFIED_TICKET'
          ),
          SpecialOperationsService.prefillFromSale(
            sale.id,
            'INTERNAL_CONTROL_RECEIPT'
          ),
        ]);
        return {
          id: sale.id,
          returnEligible:
            returnResult.status === 'fulfilled' &&
            returnResult.value.data.eligible,
          specialEligible:
            simplified.status === 'fulfilled' ||
            internal.status === 'fulfilled',
        };
      })
    ).then((results) => {
      if (!cancelled) {
        setReturnEligibility(
          Object.fromEntries(results.map((r) => [r.id, r.returnEligible]))
        );
        setSpecialOperationEligibility(
          Object.fromEntries(results.map((r) => [r.id, r.specialEligible]))
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [filteredSaleList]);

  const openDteError = async (saleId: number) => {
    setDteErrorOpen(true);
    setDteErrorLoading(true);
    setDteErrorUnavailable(false);
    setDteError(null);
    try {
      const response = await SaleService.getDteError(saleId);
      setDteError(response.data);
    } catch (error: any) {
      if (error?.response?.status === 404) setDteErrorUnavailable(true);
      else
        showAlert(
          'error',
          'Error',
          'No se pudo consultar la respuesta de Hacienda.'
        );
    } finally {
      setDteErrorLoading(false);
    }
  };

  const saveSupportPatch = async (data: SaleSupportPatch) => {
    if (supportSaleId === null) return;
    try {
      const response = await SaleService.updateBySupport(supportSaleId, data);
      showAlert(
        'success',
        'Corrección guardada',
        `Cambio auditado${
          response.data?.auditId ? ` (auditoría ${response.data.auditId})` : ''
        }.`
      );
      setSupportSaleId(null);
      handlerGetFilteredSales(filters, currentPage, pageSize);
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        status === 400
          ? 'Sin cambios para guardar.'
          : status === 403
          ? 'No tiene permiso para realizar esta corrección.'
          : status === 404
          ? 'La venta no existe.'
          : 'No se pudo guardar la corrección.';
      showAlert('error', 'Corrección no guardada', message);
      throw error;
    }
  };

  const {
    base64Pdf,
    setBase64Pdf,
    handleViewDocument: handleCustomViewDocument,
    fileName,
  } = useSalePreviewHandler();

  const { base64Dte, handleViewDte } = useSaleDtePreview();

  const { page } = useParams();
  const currentPage = page ? parseInt(page, 10) : 0;

  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const handleFilterChange = (newFilters: any) => {
    const payload = buildSaleFilterPayload(newFilters);
    if (!permissions.canSupportView) {
      delete payload.status;
      delete payload.includeDraftCancelled;
    }
    setFilters(payload);
    setFiltersApplied(true);
    handlerGetFilteredSales(payload, 0, pageSize);
  };

  const handlePageChange = (page: number) => {
    handlerGetFilteredSales(filters, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);
    handlerGetFilteredSales(filters, 0, size);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredSales(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchDocumentTypeDropdown();
    handlerGetCustomersForDropdown();
    fetchPdfTypeDropdown();
    fetchCodesByModuleAndTarget('SALES', 'DOCUMENT_TYPE');
  }, []);

  const getActions = (sourceRow: any) => {
    const row = {
      ...sourceRow,
      returnEligible: returnEligibility[sourceRow.id],
      specialOperationEligible: specialOperationEligibility[sourceRow.id],
    };
    return getSaleTableActions({
      row,
      permissions,
      handlers: {
        onView: handlerViewSaleById,
        onCancel: loadPrefillFromSale,
        onContinue: handlerGetSaleToContinue,
        onDownloadPdf: downloadInvoicePdf,
        onConsultDTE: handlerConsultDTE,
        onExportJson: handlerDownloadJson,
        onViewInvalidDte: handleViewDte,
        onDeleteRejected: handlerDeleteRejectedSale,
        onResendDTE: (id) => handlerResendDTE(id, openDteError),
        onSendEmail: sendSaleEmail,
        onEdit: handlerGetSaleForEdit,
        onViewDteError: openDteError,
        onSupportEdit: setSupportSaleId,
        onCreateReturn: (id: number) => navigate(`/sales/returns/${id}`),
        onCreateSpecialOperation: setSpecialOperationSaleId,

        onReprintTicket: ({ type, id }) => {
          void requestLegacyPrint(type as 'SALE', id).catch(() => undefined);
        },

        // 🔥 NUEVO HANDLER
        onForceAnnul: (id: number) => handlerForceAnnulRejectedSale(id), // refresca tabla después
      },
    });
  };

  const visibleSaleList = useMemo(
    () =>
      permissions.canSupportView
        ? filteredSaleList
        : filteredSaleList.filter((sale) => sale.status !== 'DRAFT_CANCELLED'),
    [filteredSaleList, permissions.canSupportView]
  );

  const allowedCodes = getCodes('SALES', 'DOCUMENT_TYPE');

  const allowedDocumentTypes = (documentTypeDropdown ?? []).filter((doc) =>
    allowedCodes.includes(doc.code)
  );

  const filterFields = getSaleHistoryFilters(
    allowedDocumentTypes,
    customersDropdown,
    permissions.canSupportView
  );

  useEffect(() => {
    handlerGetPendingCount();
    fetchBranchDropdown();
  }, []);

  useEffect(() => {
    if (showPendingSaleModal) {
      handlerGetAllPendingSummary();
    }
  }, [showPendingSaleModal]);

  const downloadInvoicePdf = ({
    id,
    branchId,
  }: {
    id: number;
    branchId: number;
  }) => {
    setSaleMetaData(id, branchId);
    openSalePdfModal();
  };

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <AdvancedRecordTable
        columns={saleHistoryColumns}
        data={visibleSaleList}
        documentTypes={documentTypeDropdown}
        suppliers={customersDropdown}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        getActions={getActions}
        paginator={paginator}
        onResetFilters={clearFilteredSales}
        url="/sale-history"
        pageSize={pageSize}
        noDataMessage="No hay resultados. Pruebe ajustando los filtros de búsqueda."
        topRightAction={{
          label: 'Nueva Venta',
          href: '/sale-register',
        }}
        topLeftActions={[
          {
            label: 'Ventas pendientes',
            onClick: () => openPendingSaleModal(),
            count: pendingSaleCount,
          },
        ]}
      />

      <SaleViewModal isOpen={saleToViewLoaded} onClose={handleCloseViewModal} />

      <PdfModal
        isOpen={showSalePdfModal}
        onClose={closeSalePdfModal}
        documentOptions={pdfTypeDropdown}
        actions={pdfModalActions}
        onViewDocument={handleCustomViewDocument}
        onReset={() => {
          clearSaleMetaData();
          setBase64Pdf(undefined);
        }}
        base64Pdf={base64Pdf}
        fileName={fileName}
      />

      <DtePdfModal
        isOpen={showDtePdfModal}
        onClose={closeDtePdfModal}
        base64Pdf={base64Dte}
        title="Vista previa DTE"
        maxWidth="max-w-6xl"
        maxHeight="max-h-[90vh]"
      />

      <PendingSaleModal
        isOpen={showPendingSaleModal}
        onClose={closePendingSaleModal}
        sales={pendingSaleList}
        branches={branchDropdown}
      />
      
      <DteErrorModal
        isOpen={dteErrorOpen}
        detail={dteError}
        loading={dteErrorLoading}
        unavailable={dteErrorUnavailable}
        onDownload={
          dteError
            ? () =>
                void SaleService.downloadDteResponse(
                  Number(dteError.documentId)
                )
            : undefined
        }
        onClose={() => setDteErrorOpen(false)}
      />
      <SaleSupportModal
        isOpen={supportSaleId !== null}
        onClose={() => setSupportSaleId(null)}
        onSubmit={saveSupportPatch}
      />
      <SpecialOperationFromSaleModal
        saleId={specialOperationSaleId}
        onClose={() => setSpecialOperationSaleId(null)}
      />
    </WorkspaceLayout>
  );
};

export default SaleHistory;
